import {
  AuthFill,
  Button,
  CompanyName,
  Email,
  FirstName,
  LastName,
  MarketingConsentSimple,
  Postcode,
  Title,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import closeOverlay from '@utils/helpers/closeOverlay'
import { getContentFile, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { FleetNewsletterSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

const FleetNewsletter = ({ content, tracking, brand }) => {
  const [submitResponse, setSubmitResponse] = useState('')
  const initialValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    postcode: '',
    company_name: '',
    consent: false,
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(FleetNewsletterSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const onSubmit = async (data) => {
    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/fleet-newsletter`,
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          data.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
          brand === 'lexus',
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
        background={content.background}
        color={content.colour}
      />
      <FormWrapper>
        <FormStep
          title={content.details.title}
          subtitle={content.details.subtitle}
          active={true}
        >
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <Title />
              <FirstName />
              <LastName />
              <CompanyName />
              <Email />
              <Postcode />
              <MarketingConsentSimple />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'newsletter-fleet'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

export default FleetNewsletter
